import { Component, ErrorInfo, ReactNode } from 'react';
import ErrorFallback from './ErrorFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * When this changes while the fallback is showing, the boundary clears its
   * error and renders its children again. ProtectedLayout passes the router's
   * location.key, so any navigation (including a click on the link for the
   * page already open) recovers from a crashed page.
   */
  resetKey?: unknown;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component that catches JavaScript errors in child components.
 * Displays a fallback UI instead of crashing the entire application.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // In production, you could send this to an error reporting service
    // Example: errorReportingService.log({ error, errorInfo });
  }

  componentDidUpdate(prevProps: Props, prevState: State): void {
    // Only reset an error that was already showing before this update. An
    // error caught during the same update that changed the key (navigating
    // onto a page that crashes) must keep its fallback, or it would loop.
    if (
      this.state.hasError &&
      prevState.hasError &&
      !Object.is(prevProps.resetKey, this.props.resetKey)
    ) {
      this.handleReset();
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
